import { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import user from 'assets/header/user.svg';
import Pagination from 'common-ui/table/Pagination';
import TextEditor from 'common-ui/textEditor';
import {
  getAppointmentComments,
  sendAppointmentComment,
  sendComment,
} from 'features/projectManagement/ProjectManagementActions';
import {
  selectAppointmentComments,
  selectLoading,
  selectSingleTicket,
  selectSuccess,
} from 'features/projectManagement/ProjectManagementSlice';
import { Avatar } from 'pages/teamManagement/membership/Membership.styles';
import { readableDateFormat } from 'utils/dateUtils';
import * as Yup from 'yup';

import { CommentText, DetailInfo } from '../singleTicketView/SingleTicketView.styles';
import {
  CommentColumn,
  CommentCountTitle,
  CommentWrapper,
  Container,
  Row,
  RowSpace,
  TimeText,
} from './Comment.styles';

const schema = Yup.object({
  comment: Yup.string()
    .required('Comment is required')
    .test(
      'min-length',
      'Comment must be at least 3 characters long',
      (value) => getTextLength(value || '') >= 3 || getTextLength(value || '') == 0
    )
    .max(10000),
});

const getTextLength = (html = '') => html.replace(/<[^>]*>/g, '').trim().length;

const Comment = ({ type = 'ticket' }) => {
  const dispatch = useDispatch();
  const { uuid } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const isLoading = useSelector(selectLoading);
  const ticket = useSelector(selectSingleTicket);
  const appointmentComments = useSelector(selectAppointmentComments);
  const success = useSelector(selectSuccess);

  const isAppointment = type === 'appointment';
  const comments = isAppointment ? appointmentComments : ticket?.comments;
  const commentsPerPage = 8;
  const totalPages = Math.ceil((comments?.length || 0) / commentsPerPage);

  const {
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: { comment: '' },
  });

  const commentValue = watch('comment');
  const isSendDisabled = getTextLength(commentValue) < 3;

  const handleSendCommentClick = (commentHtml) => {
    if (isAppointment) {
      dispatch(sendAppointmentComment({ text: commentHtml, uuid })).then(() => {
        reset({ comment: '' }, { keepErrors: false, shouldFocus: false });
      });
    } else {
      dispatch(sendComment({ text: commentHtml, uuid })).then(() => {
        reset({ comment: '' }, { keepErrors: false, shouldFocus: false });
      });
    }
  };

  const startIndex = (currentPage - 1) * commentsPerPage;
  const endIndex = startIndex + commentsPerPage;
  const paginatedComments = comments?.slice(startIndex, endIndex);

  const onPageChange = (page) => setCurrentPage(page);

  useEffect(() => {
    if (isAppointment && uuid) {
      dispatch(getAppointmentComments(uuid));
    }
  }, [dispatch, isAppointment, uuid]);

  useEffect(() => {
    if (success.comment) {
      if (isAppointment && uuid) {
        dispatch(getAppointmentComments(uuid));
      }
    }
  }, [success.comment, reset, dispatch, isAppointment, uuid]);
  return (
    <Container>
      <Controller
        control={control}
        name="comment"
        defaultValue=""
        render={({ field }) => (
          <TextEditor
            {...field}
            label="Comments"
            errorText={errors.comment?.message}
            value={field.value}
            placeholder="Type your comment here..."
            sendButton={true}
            sendBtnDisabled={isSendDisabled}
            loading={isLoading.comment}
            handleSendClick={(value) => {
              if (getTextLength(value) < 3) return;
              handleSendCommentClick(value);
            }}
            onChange={(value) => field.onChange(value)}
          />
        )}
      />

      {comments?.length > 0 && (
        <>
          <CommentCountTitle>All comments ({comments?.length ?? 0})</CommentCountTitle>
          {paginatedComments?.map((comment, index) => (
            <CommentWrapper key={comment.id || `${comment.createdAt}-${index}`}>
              <CommentColumn>
                <RowSpace>
                  <Row>
                    <Avatar src={comment?.user?.photo ?? user} alt={comment?.name || 'User'} />
                    <DetailInfo>{comment?.user?.name}</DetailInfo>
                  </Row>
                  <TimeText>{readableDateFormat(comment.createdAt)}</TimeText>
                </RowSpace>
                <CommentText dangerouslySetInnerHTML={{ __html: comment.text }} />
              </CommentColumn>
            </CommentWrapper>
          ))}

          {totalPages > 1 && (
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={onPageChange}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default Comment;
